import { Folder, X } from "lucide-react";
import { useTranslations } from "use-intl";
import { cn } from "@/utils";

export interface FolderChipProps {
  /** Forward-slash or " / " separated folder path string */
  path: string;
  /** If provided, renders clear (×) button */
  onClear?: () => void;
  /** If provided, the chip body becomes a button navigating to the folder */
  onClick?: () => void;
  /** Accessible label for the chip body button when onClick is provided */
  clickAriaLabel?: string;
  /** Accessible label for the clear button (defaults to media namespace key) */
  clearAriaLabel?: string;
  className?: string;
}

/**
 * FolderChip — breadcrumb-as-chip with optional clear and optional click navigation.
 * Spec: 28px height, radius-full, 12px font weight-500, max-width 280px truncate.
 * Bg: success 10% tint; border: success 25% + border mix.
 * When onClick is provided the chip body renders as a <button> for accessibility.
 */
export function FolderChip({ path, onClear, onClick, clickAriaLabel, clearAriaLabel, className }: FolderChipProps) {
  const t = useTranslations("media");

  const chipContent = (
    <>
      <Folder
        size={12}
        strokeWidth={1.6}
        aria-hidden
        className="shrink-0 text-[var(--success)]"
      />
      <span className="truncate">{path}</span>
    </>
  );

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5",
        "h-[28px] rounded-[var(--radius-full)]",
        onClear ? "pr-1" : "pr-2.5",
        "text-[12px] font-medium",
        "max-w-[280px]",
        "border",
        "bg-[color-mix(in_oklch,var(--success)_10%,white)]",
        "border-[color-mix(in_oklch,var(--success)_25%,var(--border))]",
        "text-[oklch(0.30_0.14_150)]",
        "select-none",
        className
      )}
    >
      {onClick ? (
        <button
          type="button"
          aria-label={clickAriaLabel}
          onClick={onClick}
          className={cn(
            "inline-flex items-center gap-1.5 pl-2",
            "hover:opacity-80 transition-opacity duration-[120ms]",
            "border-0 bg-transparent p-0 cursor-pointer",
            onClear ? "" : "pr-2.5"
          )}
        >
          {chipContent}
        </button>
      ) : (
        <span className="inline-flex items-center gap-1.5 pl-2">
          {chipContent}
        </span>
      )}
      {onClear && (
        <button
          type="button"
          aria-label={clearAriaLabel ?? t("filter.folder_chip_clear_aria")}
          onClick={onClear}
          className={cn(
            "inline-flex items-center justify-center shrink-0",
            "w-4 h-4 rounded-full",
            "text-[var(--success)] opacity-70",
            "hover:opacity-100 hover:bg-[color-mix(in_oklch,var(--success)_18%,white)]",
            "transition-opacity duration-[120ms]",
            "border-0 p-0"
          )}
        >
          <X size={10} strokeWidth={2} aria-hidden />
        </button>
      )}
    </span>
  );
}
